import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  ActivityItem, Badge, DealSubmission, Expertise, LeaderboardEntry, PointsSummary, PointsTransaction, Reward,
  RewardRedemption, StorePreview, SubmissionInput, SubmissionPreview, UserStats,
} from '../models/community';
import { AuthService } from './auth.service';

// Espace membre (API /community, protégée par JWT) : partage de deals, Points Club, cadeaux, statistiques
@Injectable({
  providedIn: 'root'
})
export class CommunityService {

  private host = environment.host + '/community';

  // Résumé des points partagé entre le menu du compte, la page Points Club et la boutique
  private points = new BehaviorSubject<PointsSummary | null>(null);
  points$ = this.points.asObservable();

  constructor(private httpClient: HttpClient, private authService: AuthService) {
    this.authService.loggedIn$.subscribe(loggedIn => {
      if (!loggedIn) {
        this.points.next(null);
      }
    });
  }

  // ----- Deals partagés -----

  preview(url: string): Observable<SubmissionPreview> {
    return this.httpClient.post<SubmissionPreview>(this.host + '/submissions/preview', { url });
  }

  storePreview(storeName: string): Observable<StorePreview> {
    return this.httpClient.post<StorePreview>(this.host + '/submissions/store-preview', { storeName });
  }

  submit(input: SubmissionInput): Observable<DealSubmission> {
    return this.httpClient.post<DealSubmission>(this.host + '/submissions', input);
  }

  mySubmissions(): Observable<DealSubmission[]> {
    return this.httpClient.get<DealSubmission[]>(this.host + '/submissions');
  }

  cancelSubmission(id: string): Observable<{ deleted: boolean }> {
    return this.httpClient.delete<{ deleted: boolean }>(this.host + '/submissions/' + id);
  }

  // ----- Points Club -----

  loadPoints(): Observable<PointsSummary> {
    return this.httpClient.get<PointsSummary>(this.host + '/points').pipe(tap(summary => this.points.next(summary)));
  }

  pointsHistory(limit = 50): Observable<PointsTransaction[]> {
    return this.httpClient.get<PointsTransaction[]>(this.host + '/points/history', { params: { limit } });
  }

  leaderboard(): Observable<LeaderboardEntry[]> {
    return this.httpClient.get<LeaderboardEntry[]>(this.host + '/leaderboard');
  }

  // ----- Cadeaux -----

  rewards(): Observable<Reward[]> {
    return this.httpClient.get<Reward[]>(this.host + '/rewards');
  }

  redeem(rewardId: string): Observable<RewardRedemption> {
    return this.httpClient.post<RewardRedemption>(this.host + '/rewards/' + rewardId + '/redeem', {});
  }

  myRedemptions(): Observable<RewardRedemption[]> {
    return this.httpClient.get<RewardRedemption[]>(this.host + '/redemptions');
  }

  // ----- Profil -----

  stats(): Observable<UserStats> {
    return this.httpClient.get<UserStats>(this.host + '/stats');
  }

  badges(): Observable<Badge[]> {
    return this.httpClient.get<Badge[]>(this.host + '/badges');
  }

  expertise(): Observable<Expertise> {
    return this.httpClient.get<Expertise>(this.host + '/expertise');
  }

  activity(): Observable<ActivityItem[]> {
    return this.httpClient.get<ActivityItem[]>(this.host + '/activity');
  }
}
